import { useState, useEffect } from "react";
import { X, Mail, Loader2, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getInvitedGuests, addInvitedGuest, removeInvitedGuest } from "@/services/invited-guests";

interface Props {
  collectionId: string;
  collectionTitle?: string;
  shareSlug?: string;
  organizerName?: string;
  onClose: () => void;
}

const InviteModal = ({ collectionId, collectionTitle, shareSlug, organizerName, onClose }: Props) => {
  const { toast } = useToast();
  const [guests, setGuests] = useState<{ id: string; email: string }[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState<string | null>(null);

  useEffect(() => {
    getInvitedGuests(collectionId).then(({ data }) => {
      setGuests(data.map((g) => ({ id: g.id, email: g.email })));
      setLoading(false);
    });
  }, [collectionId]);

  const sendInvitationEmail = async (email: string) => {
    // Always use the published domain so recipients land on the real site
    const cardUrl = shareSlug
      ? `https://joyapp.lovable.app/card/${shareSlug}`
      : undefined;

    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "guest-invitation",
          recipientEmail: email,
          idempotencyKey: `invite-${collectionId}-${email}-${Date.now()}`,
          templateData: {
            organizerName: organizerName || undefined,
            occasionTitle: collectionTitle || undefined,
            cardUrl,
          },
        },
      });
    } catch (err) {
      console.error("Failed to send invitation email", err);
    }
  };

  const handleAdd = async () => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      toast({ variant: "destructive", title: "Invalid email", description: "Please enter a valid email address." });
      return;
    }
    if (guests.some((g) => g.email === trimmed)) {
      setEmailInput("");
      return;
    }
    setAdding(true);
    const { data, error } = await addInvitedGuest(collectionId, trimmed);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not add guest." });
    } else if (data) {
      setGuests((prev) => [...prev, { id: (data as any).id, email: trimmed }]);
      await sendInvitationEmail(trimmed);
      toast({ title: "Invited!", description: `${trimmed} has been invited and will receive an email.` });
    }
    setEmailInput("");
    setAdding(false);
  };

  const handleRemove = async (id: string, email: string) => {
    const { error } = await removeInvitedGuest(id);
    if (!error) {
      setGuests((prev) => prev.filter((g) => g.id !== id));
      toast({ title: "Removed", description: `${email} no longer has access.` });
    }
  };

  const handleResend = async (email: string) => {
    setResending(email);
    await sendInvitationEmail(email);
    toast({ title: "Resent!", description: `Invitation resent to ${email}.` });
    setResending(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
      >
        {/* Header */}
        <div className="flex items-center justify-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-foreground font-serif">Invite People</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground absolute top-3 right-4"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Add email */}
          <div>
            <p className="text-sm font-semibold text-foreground mb-1">Add by email</p>
            <p className="text-xs text-muted-foreground mb-3">
              Invited guests will receive an email invitation with a link to the event page.
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="guest@example.com"
                className="flex-1 h-10 text-sm"
                disabled={adding}
              />
              <Button
                onClick={handleAdd}
                disabled={adding || !emailInput.trim()}
                size="sm"
                className="h-10 rounded-full px-5"
              >
                {adding ? <Loader2 size={14} className="animate-spin" /> : "Add"}
              </Button>
            </div>
          </div>

          {/* Guest list */}
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">
              Invited guests ({guests.length})
            </p>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-muted-foreground" />
              </div>
            ) : guests.length === 0 ? (
              <div className="text-center py-8">
                <Mail size={28} className="mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-xs text-muted-foreground">No guests invited yet</p>
              </div>
            ) : (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {guests.map((g) => (
                  <div
                    key={g.id}
                    className="flex items-center justify-between bg-muted/50 rounded-xl px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <Mail size={14} />
                      </div>
                      <span className="text-sm text-foreground truncate">{g.email}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ms-2">
                      <button
                        onClick={() => handleResend(g.email)}
                        disabled={resending === g.email}
                        className="text-muted-foreground hover:text-primary transition-colors p-1"
                        title="Resend invitation"
                      >
                        {resending === g.email ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <RotateCw size={14} />
                        )}
                      </button>
                      <button
                        onClick={() => handleRemove(g.id, g.email)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InviteModal;
